import html2canvas from "html2canvas";
import {
  applyBackgroundStyles,
  getHtml2CanvasBackgroundColor,
} from "../ProjectV2/utils/projectBackground";

const RENDER_SCALE = 2; // render 2× for sharpness, then downscale back

/**
 * Downscales (or resizes) a canvas to destWidth × destHeight using OffscreenCanvas.
 */
async function resizeCanvasToBlob(sourceCanvas, destWidth, destHeight) {
  const offscreen = new OffscreenCanvas(destWidth, destHeight);
  const ctx = offscreen.getContext("2d");

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(sourceCanvas, 0, 0, destWidth, destHeight);
  return offscreen.convertToBlob({ type: "image/png" });
}

function isBulletFlexLayout(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.matches(".bullet") || el.classList.contains("bullet")) return true;
  if (el.parentElement?.querySelector(":scope > .bullet")) return true;
  const wrap = el.closest(".flex-wrap");
  return Boolean(wrap && wrap.querySelector(".bullet"));
}

function isNameOnlyCreditName(el) {
  // NameOnly2 grid: .overlaytext[display=grid] > .flex.flex-col > p
  // Freezing those single-line names is unnecessary and can skew capture height.
  const col = el.parentElement;
  const grid = col?.parentElement;
  if (!grid?.classList?.contains("overlaytext")) return false;
  try {
    return getComputedStyle(grid).display === "grid";
  } catch {
    return false;
  }
}

function isLeafTextContainer(el) {
  if (!el || el.nodeType !== 1) return false;
  if (el.closest("[data-html2canvas-ignore]")) return false;
  if (isBulletFlexLayout(el)) return false;
  if (isNameOnlyCreditName(el)) return false;
  if (el.matches("img, svg, canvas, video, br, script, style")) return false;
  if (el.querySelector("img, svg, canvas, video, p, div, h1, h2, h3, h4, li, table, tr, td")) {
    return false;
  }
  const text = el.textContent;
  return Boolean(text && text.trim());
}

/**
 * Read the live (browser) line breaks of an element by walking text-node
 * glyphs and watching getClientRects() Y changes. This is the preview wrap.
 */
export function getWrappedTextLines(el) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  const lines = [];
  let current = "";
  let lastTop = null;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const value = node.nodeValue;
    if (!value) continue;

    for (let i = 0; i < value.length; i += 1) {
      range.setStart(node, i);
      range.setEnd(node, i + 1);
      const rect = range.getClientRects()[0];
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        current += value[i];
        continue;
      }

      if (
        lastTop !== null &&
        Math.abs(rect.top - lastTop) > Math.max(2, rect.height * 0.35)
      ) {
        lines.push(current);
        current = value[i];
      } else {
        current += value[i];
      }
      lastTop = rect.top;
    }
  }

  if (current !== "" || lines.length === 0) lines.push(current);

  let tail = el.lastChild;
  while (tail && tail.nodeType === 3 && !String(tail.nodeValue || "").trim()) {
    tail = tail.previousSibling;
  }
  if (tail && tail.nodeName === "BR") lines.push("");

  return lines;
}

function applyFrozenLines(el, lines) {
  const fragment = document.createDocumentFragment();
  lines.forEach((line, i) => {
    if (i > 0) fragment.appendChild(document.createElement("br"));
    const span = document.createElement("span");
    span.setAttribute("data-frozen-line", "true");
    span.style.whiteSpace = "nowrap";
    span.textContent = line;
    fragment.appendChild(span);
  });
  el.replaceChildren(fragment);
}

/**
 * Snapshot live wrapping on every leaf text node, then replace it with
 * explicit nowrap lines + <br>. html2canvas re-lays out text with its own
 * glyph metrics; without this, a word like "Mark" can jump up a line.
 */
export function freezePreviewWrapping(root) {
  if (!root) return () => {};

  // Freeze wrapping <p> credit lines (song title/details, blurbs).
  // Name-only paragraph bullets live in a flex-wrap + CSS grid; rewriting
  // those nodes stacks every name on top of each other in html2canvas.
  // Do not freeze role/name spans — rewriting those table cells stacks
  // role and name on top of each other in the export.
  const candidates = [...root.querySelectorAll("p")].filter(isLeafTextContainer);

  const snapshots = candidates.map((el) => {
    const lines = getWrappedTextLines(el);
    return {
      el,
      html: el.innerHTML,
      whiteSpace: el.style.whiteSpace,
      width: el.style.width,
      maxWidth: el.style.maxWidth,
      boxSizing: el.style.boxSizing,
      flexShrink: el.style.flexShrink,
      lines,
    };
  });

  snapshots.forEach((snap) => {
    // Use layout width (offsetWidth), not getBoundingClientRect — export
    // applies transform: scale(videoWidth/800) on the preview content, and
    // getBoundingClientRect includes that scale. Freezing the scaled px width
    // into an unscaled layout box overflows the flex row and shifts blurbs
    // (and other centered <p> blocks) off-center in the PNG.
    const liveWidth = snap.el.offsetWidth;
    const inSongColumn = Boolean(snap.el.closest(".songColumnsRow"));
    if (liveWidth > 0) {
      snap.el.style.boxSizing = "border-box";
      snap.el.style.maxWidth = `${liveWidth}px`;
      if (inSongColumn) {
        snap.el.style.width = "100%";
      } else {
        snap.el.style.width = `${liveWidth}px`;
        snap.el.style.flexShrink = "0";
      }
    }
    applyFrozenLines(snap.el, snap.lines);
  });

  return () => {
    snapshots.forEach((snap) => {
      snap.el.innerHTML = snap.html;
      snap.el.style.whiteSpace = snap.whiteSpace;
      snap.el.style.width = snap.width;
      snap.el.style.maxWidth = snap.maxWidth;
      snap.el.style.boxSizing = snap.boxSizing;
      snap.el.style.flexShrink = snap.flexShrink;
    });
  };
}

function assignStyles(el, styles) {
  Object.keys(styles).forEach((key) => {
    el.style[key] = styles[key];
  });
}

/**
 * Pin role/name glyphs to the live preview boxes so html2canvas cannot
 * re-align table cells. Uses layout offsets (offsetTop/Left/Width), not
 * getBoundingClientRect, so export transform:scale does not collapse Y or
 * shrink widths. Frozen spans are nowrap so word-break cannot split the
 * last letter (DIRECTOR/S).
 */
function layoutOffsetInTd(span, td) {
  if (span.offsetParent === td) {
    return { top: span.offsetTop, left: span.offsetLeft };
  }
  const parent = span.offsetParent;
  if (parent && td.offsetParent === parent) {
    return {
      top: span.offsetTop - td.offsetTop - td.clientTop,
      left: span.offsetLeft - td.offsetLeft - td.clientLeft,
    };
  }
  return { top: span.offsetTop, left: span.offsetLeft };
}

export function freezeRoleNameToPreview(root) {
  if (!root) return () => {};

  const jobs = [];
  root.querySelectorAll("table.roleName_tr").forEach((table) => {
    const cells = [...table.querySelectorAll("td.roleName_td")]
      .map((td) => {
        const span = [...td.children].find((el) => el.tagName === "SPAN");
        if (!span || !span.textContent.replace(/\s/g, "")) return null;

        const { top, left } = layoutOffsetInTd(span, td);

        return {
          td,
          span,
          tdWidth: td.offsetWidth,
          tdHeight: td.offsetHeight,
          top,
          left,
          spanWidth: Math.ceil(span.offsetWidth),
          spanHeight: Math.ceil(span.offsetHeight),
          tdStyle: {
            position: td.style.position,
            width: td.style.width,
            height: td.style.height,
            verticalAlign: td.style.verticalAlign,
          },
          spanStyle: {
            position: span.style.position,
            top: span.style.top,
            left: span.style.left,
            width: span.style.width,
            height: span.style.height,
            margin: span.style.margin,
            whiteSpace: span.style.whiteSpace,
            wordBreak: span.style.wordBreak,
          },
        };
      })
      .filter(Boolean);

    if (!cells.length) return;
    jobs.push({
      table,
      tableWidth: table.offsetWidth,
      tableHeight: table.offsetHeight,
      tableStyle: {
        position: table.style.position,
        width: table.style.width,
        height: table.style.height,
      },
      cells,
    });
  });

  jobs.forEach(({ table, tableWidth, tableHeight, cells }) => {
    table.style.position = "relative";
    table.style.width = `${tableWidth}px`;
    table.style.height = `${tableHeight}px`;
    cells.forEach((cell) => {
      cell.td.style.position = "relative";
      cell.td.style.width = `${cell.tdWidth}px`;
      cell.td.style.height = `${cell.tdHeight}px`;
      cell.td.style.verticalAlign = "top";
    });
    cells.forEach((cell) => {
      cell.span.style.position = "absolute";
      cell.span.style.top = `${cell.top}px`;
      cell.span.style.left = `${cell.left}px`;
      cell.span.style.width = `${cell.spanWidth}px`;
      cell.span.style.height = `${cell.spanHeight}px`;
      cell.span.style.margin = "0";
      cell.span.style.whiteSpace = "nowrap";
      cell.span.style.wordBreak = "normal";
    });
  });

  return () => {
    jobs.forEach(({ table, tableStyle, cells }) => {
      assignStyles(table, tableStyle);
      cells.forEach((cell) => {
        assignStyles(cell.td, cell.tdStyle);
        assignStyles(cell.span, cell.spanStyle);
      });
    });
  };
}

function getCaptureSize(element) {
  const root = element.getBoundingClientRect();
  let width = Math.max(element.offsetWidth, element.scrollWidth, 1);
  let height = Math.max(element.offsetHeight, element.scrollHeight, 1);

  element.querySelectorAll("*").forEach((child) => {
    const box = child.getBoundingClientRect();
    if (!box.width && !box.height) return;
    width = Math.max(width, box.right - root.left);
    height = Math.max(height, box.bottom - root.top);
  });

  // Glyph boxes catch descenders that element boxes can miss.
  const range = document.createRange();
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!String(node.nodeValue || "").trim()) continue;
    range.selectNodeContents(node);
    for (const box of range.getClientRects()) {
      if (!box.width && !box.height) continue;
      width = Math.max(width, box.right - root.left);
      height = Math.max(height, box.bottom - root.top);
    }
  }

  return {
    width: Math.max(1, Math.ceil(width)),
    height: Math.max(1, Math.ceil(height) + 2),
  };
}

/**
 * html2canvas mis-aligns nodes inside scrolled preview ancestors (phantom top
 * gap + clipped bottom). Clone into a fixed host at (0,0) so paint matches layout.
 */
function stripFocusOverlayClasses(root) {
  if (!root) return;
  const nodes = [
    root,
    ...root.querySelectorAll("[class*='focusDiv'], [class*='CardfocusDiv']"),
  ];
  nodes.forEach((el) => {
    if (!el?.classList) return;
    el.classList.remove("focusDiv", "CardfocusDiv");
    if (el.classList.contains("viewBlockfocusDiv")) {
      el.classList.remove("viewBlockfocusDiv");
      el.classList.add("viewBlock");
    }
    if (el.classList.contains("viewBlockCardfocusDiv")) {
      el.classList.remove("viewBlockCardfocusDiv");
      el.classList.add("viewBlock");
    }
  });
}

function mountIsolatedClone(element) {
  const liveWidth = Math.max(element.offsetWidth, 1);
  const host = document.createElement("div");
  host.setAttribute("data-render-capture-host", "true");
  host.style.cssText = [
    "position:fixed",
    "left:0",
    "top:0",
    "z-index:-1",
    "pointer-events:none",
    `width:${liveWidth}px`,
    "margin:0",
    "padding:0",
    "border:none",
    "background:transparent",
  ].join(";");

  const clone = element.cloneNode(true);
  stripFocusOverlayClasses(clone);
  clone.style.width = `${liveWidth}px`;
  clone.style.maxWidth = `${liveWidth}px`;
  clone.style.transform = "none";
  clone.style.zoom = "1";
  clone.style.border = "none";
  clone.style.boxShadow = "none";
  clone.style.borderRadius = "0";
  clone.style.margin = "0";
  clone.style.position = "static";
  clone.style.left = "auto";
  clone.style.top = "auto";
  clone.style.overflow = "visible";

  host.appendChild(clone);

  // Empty heading/subheading wrappers can still paint a line-box in html2canvas.
  clone.querySelectorAll('[id^="heading_"], [id^="subheading_"]').forEach((el) => {
    if (!String(el.textContent || "").trim()) el.remove();
  });

  document.body.appendChild(host);
  void clone.offsetHeight;
  pinNameOnlyCaptureWidths(clone);

  return {
    host,
    clone,
    dispose() {
      host.remove();
    },
  };
}

/** Percent name-only widths must stay at the preview px box during capture. */
function pinNameOnlyCaptureWidths(root) {
  root.querySelectorAll(".overlaytext, [id^='list']").forEach((el) => {
    const liveWidth = el.offsetWidth;
    if (liveWidth <= 0) return;
    el.style.boxSizing = "border-box";
    el.style.width = `${liveWidth}px`;
    el.style.maxWidth = `${liveWidth}px`;
  });
}

/**
 * Master capture: empty span + FontAwesome ::after as inline-block on the
 * name baseline, plus a 0.05em top nudge. Do not give the span a 1em height
 * or replace ::after with text — html2canvas paints that glyph at the top.
 */
function alignBulletsForCapture(root) {
  const bullets = [...root.querySelectorAll("span.bullet")];
  if (!bullets.length) return;

  bullets.forEach((span) => {
    span.style.display = "inline-block";
    span.style.width = "1.2em";
    span.style.height = "auto";
    span.style.lineHeight = "normal";
    span.style.marginTop = span.style.marginTop || "0.05em";
    span.style.padding = "0";
    span.style.position = "static";
    span.style.top = "auto";
    span.style.verticalAlign = "baseline";
    span.style.textAlign = "center";
    span.style.alignItems = "unset";
    span.style.alignSelf = "unset";
    span.style.justifyContent = "unset";
    span.style.backgroundColor = "transparent";
  });
}

const CAPTURE_SLACK_PX = 64;

function readViewBlockPads(clone) {
  const views = [
    ...clone.querySelectorAll(".viewBlock"),
    ...clone.querySelectorAll("[class*='viewBlock']"),
  ];
  const unique = [...new Set(views)];
  const padTop = unique.reduce((max, view) => {
    const inline = parseFloat(view.style.paddingTop);
    const computed = parseFloat(getComputedStyle(view).paddingTop);
    return Math.max(max, inline || 0, computed || 0);
  }, 0);
  const padBottom = unique.reduce((max, view) => {
    const inline = parseFloat(view.style.paddingBottom);
    const computed = parseFloat(getComputedStyle(view).paddingBottom);
    return Math.max(max, inline || 0, computed || 0);
  }, 0);
  return { views: unique, padTop, padBottom };
}

/** html2canvas drops unitless line-height. Pin heading/subheading to used px. */
function bakeHeadingMetrics(root) {
  root.querySelectorAll('[id^="heading_"], [id^="subheading_"]').forEach((block) => {
    if (!String(block.textContent || "").trim()) return;
    [block, ...block.querySelectorAll("span")].forEach((el) => {
      const computed = getComputedStyle(el);
      const fontSize = parseFloat(computed.fontSize) || 0;
      const used = computed.lineHeight;
      if (used && used !== "normal") {
        el.style.lineHeight = used;
      } else if (fontSize) {
        const unitless = parseFloat(el.style.lineHeight);
        const multiplier = unitless > 0 && unitless < 10 ? unitless : 1;
        el.style.lineHeight = `${fontSize * multiplier}px`;
      }
      const marginBottom = parseFloat(computed.marginBottom);
      if (marginBottom) el.style.marginBottom = `${marginBottom}px`;
    });
  });
}

/** Line box of heading/subheading in canvas pixels (includes line-height). */
function headingLineBox(clone, canvas) {
  const root = clone.getBoundingClientRect();
  const scale = canvas.width / Math.max(1, clone.offsetWidth);
  let top = Infinity;
  let bottom = -Infinity;
  clone.querySelectorAll('[id^="heading_"], [id^="subheading_"]').forEach((el) => {
    if (!String(el.textContent || "").trim()) return;
    const box = el.getBoundingClientRect();
    top = Math.min(top, (box.top - root.top) * scale);
    bottom = Math.max(bottom, (box.bottom - root.top) * scale);
  });
  if (!Number.isFinite(top)) return null;
  return {
    top: Math.max(0, Math.floor(top)),
    bottom: Math.min(canvas.height - 1, Math.ceil(bottom)),
  };
}

/** Extra bottom lines are empty <br>/spacers — no ink, so crop would drop them. */
function endlinesBox(clone, canvas) {
  const root = clone.getBoundingClientRect();
  const scale = canvas.width / Math.max(1, clone.offsetWidth);
  let bottom = -Infinity;
  clone.querySelectorAll('[id$="endlines"]').forEach((el) => {
    if ((el.offsetHeight || 0) < 1) return;
    const box = el.getBoundingClientRect();
    bottom = Math.max(bottom, (box.bottom - root.top) * scale);
  });
  if (!Number.isFinite(bottom)) return null;
  return {
    bottom: Math.min(canvas.height - 1, Math.ceil(bottom)),
  };
}

/** Margin lives on .viewBlock. Strip it for capture so we can add it back exactly. */
function stripMarginAndAddSlack(clone) {
  const { views, padTop, padBottom } = readViewBlockPads(clone);
  views.forEach((view) => {
    view.style.paddingTop = "0px";
    view.style.paddingBottom = "0px";
  });
  clone.querySelectorAll("p").forEach((p) => {
    p.style.marginTop = "0";
    p.style.marginBottom = "0";
  });
  clone.querySelectorAll("*").forEach((el) => {
    el.style.overflow = "visible";
  });
  clone.style.overflow = "visible";
  clone.style.paddingTop = `${CAPTURE_SLACK_PX}px`;
  clone.style.paddingBottom = `${CAPTURE_SLACK_PX}px`;
  return { padTop, padBottom };
}

function parseCssColor(color) {
  if (!color || color === "transparent") return null;
  const value = String(color).trim();
  if (value.startsWith("#")) {
    const hex = value.slice(1);
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map((ch) => ch + ch)
            .join("")
        : hex;
    const n = parseInt(full.slice(0, 6), 16);
    if (Number.isNaN(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  const match = value.match(/rgba?\(([^)]+)\)/i);
  if (!match) return null;
  const parts = match[1].split(",").map((part) => parseFloat(part));
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  if (parts[3] === 0) return null;
  return { r: parts[0], g: parts[1], b: parts[2] };
}

function verticalInkBounds(canvas, backgroundColor) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  const bg = parseCssColor(backgroundColor);

  const isInk = (index) => {
    const alpha = data[index + 3];
    if (alpha < 16) return false;
    if (!bg) return true;
    const delta =
      Math.abs(data[index] - bg.r) +
      Math.abs(data[index + 1] - bg.g) +
      Math.abs(data[index + 2] - bg.b);
    return delta > 40;
  };

  const rowHasInk = (y) => {
    const row = y * width * 4;
    for (let x = 0; x < width; x += 2) {
      if (isInk(row + x * 4)) return true;
    }
    return false;
  };

  let top = 0;
  while (top < height && !rowHasInk(top)) top += 1;
  let bottom = height - 1;
  while (bottom > top && !rowHasInk(bottom)) bottom -= 1;
  // No painted pixels (empty logo slot). Caller must drop capture slack.
  if (top >= height || top >= bottom) return { top: 0, height, empty: true };
  const guard = 4;
  const croppedTop = Math.max(0, top - guard);
  const croppedBottom = Math.min(height - 1, bottom + guard);
  return { top: croppedTop, height: croppedBottom - croppedTop + 1, empty: false };
}

/** Content box only — excludes the temporary capture slack padding. */
function boundsWithoutSlack(clone, canvas) {
  const scale = canvas.width / Math.max(1, clone.offsetWidth);
  const slackTop = (parseFloat(clone.style.paddingTop) || 0) * scale;
  const slackBottom = (parseFloat(clone.style.paddingBottom) || 0) * scale;
  const top = Math.min(canvas.height - 1, Math.max(0, Math.round(slackTop)));
  const bottom = Math.max(top, Math.round(canvas.height - slackBottom) - 1);
  return { top, height: Math.max(1, bottom - top + 1), empty: false };
}

async function composeTrimmedPng({
  canvas,
  srcTop,
  srcHeight,
  destWidth,
  contentDestHeight,
  padTopDest,
  padBottomDest,
  backgroundColor,
}) {
  const destHeight = contentDestHeight + padTopDest + padBottomDest;
  const offscreen = new OffscreenCanvas(destWidth, destHeight);
  const ctx = offscreen.getContext("2d");
  const bg = parseCssColor(backgroundColor);
  if (bg) {
    ctx.fillStyle = `rgb(${bg.r}, ${bg.g}, ${bg.b})`;
    ctx.fillRect(0, 0, destWidth, destHeight);
  } else {
    ctx.clearRect(0, 0, destWidth, destHeight);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    canvas,
    0,
    srcTop,
    canvas.width,
    srcHeight,
    0,
    padTopDest,
    destWidth,
    contentDestHeight,
  );
  const blob = await offscreen.convertToBlob({ type: "image/png" });
  return { blob, width: destWidth, height: destHeight };
}

/**
 * Captures a single DOM element as a sharp PNG Blob.
 * Pass targetWidth to scale the PNG to the export resolution without
 * reflowing the live preview (so wrapping stays identical).
 * Pass captureSize {width, height} to crop the capture to an exact box, and
 * targetHeight to pin the output height — needed when the consumer (e.g.
 * RenderX3 card frames) requires exact pixel dimensions, because measured
 * DOM extents can exceed the frame (overflowing content, sub-pixel ceil).
 */
export async function captureElementAsPng(element, options = {}) {
  const {
    scale = RENDER_SCALE,
    backgroundColor,
    backgroundStyle = null,
    targetWidth = null,
    targetHeight = null,
    captureSize = null,
  } = options;

  await document.fonts.ready;

  const isolated = mountIsolatedClone(element);
  const { clone, dispose } = isolated;

  if (backgroundStyle) {
    applyBackgroundStyles(clone, backgroundStyle);
  }

  const canvasBackgroundColor =
    backgroundColor !== undefined
      ? backgroundColor
      : backgroundStyle
        ? getHtml2CanvasBackgroundColor(backgroundStyle)
        : null;

  // Card frames pin an exact box. Scroll strips phantom html2canvas offset:
  // capture with slack, crop to ink, then restore margin_vertical as padding.
  const trimVertical = !captureSize && targetHeight == null;
  const marginPads = trimVertical
    ? stripMarginAndAddSlack(clone)
    : { padTop: 0, padBottom: 0 };

  const unfreeze = freezePreviewWrapping(clone);
  const unfreezeRoleName = freezeRoleNameToPreview(clone);
  bakeHeadingMetrics(clone);
  alignBulletsForCapture(clone);

  try {
    void clone.offsetHeight;
    const layoutWidth = Math.max(1, clone.offsetWidth);
    const { height: captureHeight } = captureSize || getCaptureSize(clone);
    const captureWidth = captureSize?.width || layoutWidth;

    const canvas = await html2canvas(clone, {
      backgroundColor: canvasBackgroundColor,
      scale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      // Native layout, not per-glyph spans — per-letter rendering changes wrap.
      letterRendering: false,
      width: captureWidth,
      height: captureHeight,
      x: 0,
      y: 0,
      scrollX: 0,
      scrollY: 0,
    });

    const destWidth = targetWidth
      ? Math.round(targetWidth)
      : Math.round(canvas.width / scale);

    if (trimVertical) {
      let bounds = verticalInkBounds(canvas, canvasBackgroundColor);
      if (bounds.empty) {
        bounds = boundsWithoutSlack(clone, canvas);
      }
      const lineBox = headingLineBox(clone, canvas);
      if (lineBox) {
        const inkBottom = bounds.top + bounds.height - 1;
        const top = Math.max(0, Math.min(bounds.top, lineBox.top));
        const bottom = Math.min(
          canvas.height - 1,
          Math.max(inkBottom, lineBox.bottom),
        );
        bounds.top = top;
        bounds.height = Math.max(1, bottom - top + 1);
      }
      const extraBox = endlinesBox(clone, canvas);
      if (extraBox) {
        const inkBottom = bounds.top + bounds.height - 1;
        const bottom = Math.min(
          canvas.height - 1,
          Math.max(inkBottom, extraBox.bottom),
        );
        bounds.height = Math.max(1, bottom - bounds.top + 1);
      }
      const contentDestHeight = Math.max(
        1,
        Math.round(bounds.height * (destWidth / canvas.width)),
      );
      const pxPerCss = destWidth / Math.max(1, clone.offsetWidth);
      const composed = await composeTrimmedPng({
        canvas,
        srcTop: bounds.top,
        srcHeight: bounds.height,
        destWidth,
        contentDestHeight,
        padTopDest: Math.round(marginPads.padTop * pxPerCss),
        padBottomDest: Math.round(marginPads.padBottom * pxPerCss),
        backgroundColor: canvasBackgroundColor,
      });
      return {
        blob: composed.blob,
        width: composed.width,
        height: composed.height,
        cssWidth: composed.width,
        cssHeight: composed.height,
      };
    }

    const destHeight = targetHeight
      ? Math.round(targetHeight)
      : Math.round(canvas.height * (destWidth / canvas.width));
    const blob = await resizeCanvasToBlob(canvas, destWidth, destHeight);

    return {
      blob,
      width: destWidth,
      height: destHeight,
      cssWidth: destWidth,
      cssHeight: destHeight,
    };
  } finally {
    unfreezeRoleName();
    unfreeze();
    dispose();
  }
}

/**
 * Solid PNG at the export resolution. Used as the lead-in and lead-out
 * frames around captured credit blocks.
 */
export async function createBlankPng({ width, height, color = "transparent" }) {
  const destWidth = Math.max(1, Math.round(width));
  const destHeight = Math.max(1, Math.round(height));
  const offscreen = new OffscreenCanvas(destWidth, destHeight);
  const ctx = offscreen.getContext("2d");
  const transparent =
    !color ||
    String(color).toLowerCase() === "transparent" ||
    String(color).toLowerCase() === "rgba(0, 0, 0, 0)";
  if (transparent) {
    ctx.clearRect(0, 0, destWidth, destHeight);
  } else {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, destWidth, destHeight);
  }
  const blob = await offscreen.convertToBlob({ type: "image/png" });
  return {
    blob,
    width: destWidth,
    height: destHeight,
    cssWidth: destWidth,
    cssHeight: destHeight,
  };
}

/**
 * Export lays out credits at previewWidth then applies transform:scale(video/preview)
 * on an inner wrapper. html2canvas mishandles that scale: bottoms clip and a
 * phantom top gap appears. Capture at unscaled layout size; targetWidth upscales.
 */
function withExportScaleDisabled(block, run) {
  const children = [...(block?.children || [])];
  const scaled = children.find((child) => {
    if (!(child instanceof HTMLElement)) return false;
    if (
      child.classList.contains("watermark") ||
      child.classList.contains("watermark_displaynone")
    ) {
      return false;
    }
    return /scale\s*\(/i.test(child.style.transform || "");
  });

  if (!scaled) return run();

  const prevTransform = scaled.style.transform;
  const prevBlockWidth = block.style.width;
  scaled.style.transform = "none";
  const contentWidth = Math.max(scaled.offsetWidth, 1);
  block.style.width = `${contentWidth}px`;

  const restore = () => {
    scaled.style.transform = prevTransform;
    block.style.width = prevBlockWidth;
  };

  try {
    const result = run();
    if (result && typeof result.then === "function") {
      return result.finally(restore);
    }
    restore();
    return result;
  } catch (error) {
    restore();
    throw error;
  }
}

/**
 * Captures every .blockWrapper element and returns ordered PNG blobs.
 */
export async function generateBlockImages(options = {}) {
  const {
    scale = RENDER_SCALE,
    onProgress,
    backgroundStyle = null,
    getBackgroundStyleForBlock = null,
    targetWidth = null,
    backgroundColor,
  } = options;

  const blocks = document.querySelectorAll(".blockWrapper");
  if (!blocks.length) throw new Error("No .blockWrapper elements found");

  // The preview may be zoomed (fullscreen fit). Zoom must be OFF while
  // capturing: html2canvas and the rect-based measurements here assume
  // unzoomed layout pixels. Resetting zoom never reflows the credits —
  // it only changes display size — so the capture itself is unaffected.
  const zoomWrapper = document.getElementById("preview-zoom-wrapper");
  const previousZoom = zoomWrapper ? zoomWrapper.style.zoom : null;
  if (zoomWrapper) zoomWrapper.style.zoom = 1;

  const previewEl = document.getElementById("preview");
  const previousScrollTop = previewEl ? previewEl.scrollTop : 0;
  if (previewEl) previewEl.scrollTop = 0;

  try {
    const results = [];
    let offsetY = 0;

    for (let idx = 0; idx < blocks.length; idx++) {
      const item = blocks[idx];
      const blockBackgroundStyle = getBackgroundStyleForBlock
        ? getBackgroundStyleForBlock(item, idx, offsetY)
        : backgroundStyle;

      const { blob, width, height, cssWidth, cssHeight } =
        await withExportScaleDisabled(item, () =>
          captureElementAsPng(item, {
            scale,
            backgroundStyle: blockBackgroundStyle,
            targetWidth,
            backgroundColor,
          }),
        );

      results.push({ blob, idx, width, height, cssWidth, cssHeight });
      onProgress?.(idx + 1, blocks.length);
      offsetY += item.offsetHeight;
    }

    return results;
  } finally {
    if (previewEl) previewEl.scrollTop = previousScrollTop;
    if (zoomWrapper) zoomWrapper.style.zoom = previousZoom ?? "";
  }
}

/**
 * Stitches multiple PNG blobs into a single vertical strip.
 */
export async function generateStitchedImage(blockImages) {
  const bitmaps = await Promise.all(
    blockImages.map(({ blob }) => createImageBitmap(blob)),
  );

  const totalWidth = Math.max(...bitmaps.map((b) => b.width));
  const totalHeight = bitmaps.reduce((sum, b) => sum + b.height, 0);

  const offscreen = new OffscreenCanvas(totalWidth, totalHeight);
  const ctx = offscreen.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, totalWidth, totalHeight);

  let y = 0;
  for (const bitmap of bitmaps) {
    ctx.drawImage(bitmap, 0, y);
    y += bitmap.height;
    bitmap.close();
  }

  return offscreen.convertToBlob({ type: "image/png" });
}

export async function generateScrollPng(options = {}) {
  const blockImages = await generateBlockImages(options);
  return generateStitchedImage(blockImages);
}
